import React from 'react';

function UsersList({ 
  users, 
  loading, 
  filters, 
  pagination, 
  onFilterChange, 
  onPageChange, 
  onEditUser, 
  onDeleteUser,
  currentUser 
}) {

  const handleSearchChange = (e) => {
    const search = e.target.value;
    onFilterChange({ search });
  };

  const handleRoleFilter = (e) => {
    const role = e.target.value;
    onFilterChange({ role });
  };

  const handleStatusFilter = (e) => {
    const is_active = e.target.value;
    onFilterChange({ is_active });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'badge bg-danger',
      editor: 'badge bg-primary',
      viewer: 'badge bg-secondary'
    };
    return badges[role] || 'badge bg-secondary';
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;

    const pages = [];
    const maxPages = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxPages / 2));
    let endPage = Math.min(pagination.pages, startPage + maxPages - 1);

    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
        <button 
          className="page-link"
          onClick={() => pagination.page > 1 && onPageChange(pagination.page - 1)}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
      </li>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${pagination.page === i ? 'active' : ''}`}>
          <button 
            className="page-link"
            onClick={() => onPageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li key="next" className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
        <button 
          className="page-link"
          onClick={() => pagination.page < pagination.pages && onPageChange(pagination.page + 1)}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </li>
    );

    return (
      <nav>
        <ul className="pagination justify-content-center">
          {pages}
        </ul>
      </nav>
    );
  };

  return (
    <div className="users-list">
      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <div className="form-group">
                <label className="form-label">Search Users</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by username or email..."
                    value={filters.search}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="form-label">Filter by Role</label>
                <select
                  className="form-select"
                  value={filters.role}
                  onChange={handleRoleFilter}
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label className="form-label">Filter by Status</label>
                <select
                  className="form-select"
                  value={filters.is_active}
                  onChange={handleStatusFilter}
                >
                  <option value="">All Statuses</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label className="form-label">Results</label>
                <div className="results-info">
                  <strong>{pagination.total}</strong> users found
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-users fa-3x text-muted mb-3"></i>
              <h5>No users found</h5>
              <p className="text-muted">Try adjusting your search criteria or create a new user.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">
                              <i className="fas fa-user-circle fa-2x text-muted"></i>
                            </div>
                            <div className="user-details">
                              <div className="user-name">
                                <strong>{user.username}</strong>
                                {user.id === currentUser?.id && (
                                  <span className="badge bg-info ms-2">You</span>
                                )}
                              </div>
                              <div className="user-email text-muted">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={getRoleBadge(user.role)}>
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${user.is_active ? 'bg-success' : 'bg-warning'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(user.last_login)}
                          </small>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(user.created_at)}
                          </small>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => onEditUser(user)}
                              title="Edit User"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            {user.id !== currentUser?.id && (
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => onDeleteUser(user.id, user.username)}
                                title="Delete User"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="pagination-wrapper mt-4">
                  <div className="pagination-info">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} results
                  </div>
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .users-list .card {
          border: none;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          border-radius: 10px;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.5rem;
        }

        .input-group-text {
          background-color: #f8f9fa;
          border-color: #dee2e6;
        }

        .form-control,
        .form-select {
          border: 2px solid #e9ecef;
          border-radius: 6px;
          padding: 0.5rem 0.75rem;
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #c9302c;
          box-shadow: 0 0 0 0.2rem rgba(201, 48, 44, 0.25);
        }

        .results-info {
          background-color: #f8f9fa;
          padding: 0.5rem 0.75rem;
          border-radius: 6px;
          font-size: 0.9rem;
          margin-top: 0.5rem;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .user-details {
          display: flex;
          flex-direction: column;
        }

        .user-name {
          font-size: 0.95rem;
          margin-bottom: 0.2rem;
        }

        .user-email {
          font-size: 0.85rem;
        }

        .table th {
          background-color: #f8f9fa;
          border-top: none;
          font-weight: 600;
          color: #495057;
          padding: 1rem 0.75rem;
        }

        .table td {
          padding: 1rem 0.75rem;
          vertical-align: middle;
        }

        .table-hover tbody tr:hover {
          background-color: #f8f9fa;
        }

        .badge {
          font-size: 0.75rem;
          font-weight: 500;
          padding: 0.35em 0.65em;
        }

        .btn-group-sm .btn {
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
          border-radius: 4px;
        }

        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .pagination-info {
          color: #6c757d;
          font-size: 0.9rem;
        }

        .pagination .page-link {
          color: #6c757d;
          border: 1px solid #dee2e6;
          padding: 0.5rem 0.75rem;
          margin: 0 0.1rem;
          border-radius: 4px;
        }

        .pagination .page-link:hover {
          color: #c9302c;
          background-color: #f8f9fa;
          border-color: #c9302c;
        }

        .pagination .page-item.active .page-link {
          background-color: #c9302c;
          border-color: #c9302c;
          color: white;
        }

        .pagination .page-item.disabled .page-link {
          color: #adb5bd;
          background-color: #fff;
          border-color: #dee2e6;
        }

        @media (max-width: 768px) {
          .pagination-wrapper {
            flex-direction: column;
            text-align: center;
          }
          
          .user-info {
            gap: 0.5rem;
          }
          
          .table-responsive {
            font-size: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
}

export default UsersList;